import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../reducers";
import { PencilerSubscription, SubscriptionOption } from "../types/subscriptions";

export type SubscriptionsSlice = {
  currentPencilerSubscription: PencilerSubscription | Record<string, never>;
  gettingCurrentPencilerSubscription: boolean;
  getCurrentPencilerSubscriptionSuccess: boolean;
  getCurrentPencilerSubscriptionError: boolean;
  getCurrentPencilerSubscriptionErrorMessage: string;
  subscriptionOptions: SubscriptionOption[];
  gettingSubscriptionOptions: boolean;
  gettingSubscriptionOptionsSuccess: boolean;
  gettingSubscriptionOptionsError: boolean;
  gettingSubscriptionOptionsErrorMessage: string;
  openAddSubscriptionModal: boolean;
  submittingAddSubscription: boolean;
  addSubscriptionModalFailed: boolean;
  addSubscriptionModalFailedMessage: string;
  openUpgradeSubscriptionModal: boolean;
  submittingUpgradeSubscription: boolean;
  upgradeSubscriptionModalFailed: boolean;
  upgradeSubscriptionModalFailedMessage: string;
  prorationCost: number | null;
  prorationPlanToId: string;
  // unused
  prorationDateTime: number | null;
  prorationToken: string | null;
  gettingProrationCost: boolean;
  getProrationCostError: boolean;
  getProrationCostErrorMessage: string;
};

const initialState: SubscriptionsSlice = {
  currentPencilerSubscription: {},
  gettingCurrentPencilerSubscription: false,
  getCurrentPencilerSubscriptionSuccess: false,
  getCurrentPencilerSubscriptionError: false,
  getCurrentPencilerSubscriptionErrorMessage: "",
  subscriptionOptions: [],
  gettingSubscriptionOptions: false,
  gettingSubscriptionOptionsSuccess: false,
  gettingSubscriptionOptionsError: false,
  gettingSubscriptionOptionsErrorMessage: "",
  openAddSubscriptionModal: false,
  submittingAddSubscription: false,
  addSubscriptionModalFailed: false,
  addSubscriptionModalFailedMessage: "",
  openUpgradeSubscriptionModal: false,
  submittingUpgradeSubscription: false,
  upgradeSubscriptionModalFailed: false,
  upgradeSubscriptionModalFailedMessage: "",
  prorationCost: null,
  prorationPlanToId: "",
  prorationDateTime: null,
  prorationToken: null,
  gettingProrationCost: false,
  getProrationCostError: false,
  getProrationCostErrorMessage: ""
};

const subscriptionsSlice = createSlice({
  name: "subscriptions",
  initialState,
  reducers: {
    gettingSubscriptionOptions(state) {
      state.subscriptionOptions = [];
      state.gettingSubscriptionOptions = true;
      state.gettingSubscriptionOptionsSuccess = false;
      state.gettingSubscriptionOptionsError = false;
      state.gettingSubscriptionOptionsErrorMessage = "";
    },
    getSubscriptionOptionsSuccess(state, action: PayloadAction<SubscriptionOption[]>) {
      state.subscriptionOptions = action.payload;
      state.gettingSubscriptionOptions = false;
      state.gettingSubscriptionOptionsSuccess = true;
      state.gettingSubscriptionOptionsError = false;
      state.gettingSubscriptionOptionsErrorMessage = "";
    },
    getSubscriptionOptionsFailed(state, action: PayloadAction<string>) {
      state.subscriptionOptions = [];
      state.gettingSubscriptionOptions = false;
      state.gettingSubscriptionOptionsSuccess = false;
      state.gettingSubscriptionOptionsError = true;
      state.gettingSubscriptionOptionsErrorMessage = action.payload;
    },
    setAddSubscriptionModalStatus(state, action: PayloadAction<boolean>) {
      state.openAddSubscriptionModal = action.payload;
    },
    // Note: action is unused
    resetAddSubscriptionModalStatus(state) {
      state.openAddSubscriptionModal = false;
      state.addSubscriptionModalFailed = false;
      state.addSubscriptionModalFailedMessage = "";
    },
    // Note: action is unused
    startAddSubscriptionSubmit(state) {
      state.submittingAddSubscription = true;
    },
    addSubscriptionCheckoutFailed(state, action: PayloadAction<string>) {
      state.submittingUpgradeSubscription = false;
      state.upgradeSubscriptionModalFailed = true;
      state.upgradeSubscriptionModalFailedMessage = action.payload;
    },
    setUpgradeSubscriptionModalStatus(state, action: PayloadAction<boolean>) {
      state.openUpgradeSubscriptionModal = action.payload;
      state.prorationCost = action.payload ? null : state.prorationCost;
      state.prorationPlanToId = action.payload ? "" : state.prorationPlanToId;
      state.prorationDateTime = action.payload ? null : state.prorationDateTime;
      state.prorationToken = action.payload ? null : state.prorationToken;
    },
    // Note: action is unused
    resetUpgradeSubscriptionModalStatus(state) {
      state.openUpgradeSubscriptionModal = false;
      state.upgradeSubscriptionModalFailed = false;
      state.upgradeSubscriptionModalFailedMessage = "";
    },
    // Note: action is unused
    startUpgradeSubscriptionSubmit(state) {
      state.submittingUpgradeSubscription = true;
    },
    upgradeSubscriptionCheckoutFailed(state, action: PayloadAction<string>) {
      state.submittingUpgradeSubscription = false;
      state.upgradeSubscriptionModalFailed = true;
      state.upgradeSubscriptionModalFailedMessage = action.payload;
    },
    gettingProrationCost(state, action: PayloadAction<string>) {
      state.prorationCost = null;
      state.prorationToken = null;
      state.prorationPlanToId = action.payload;
      state.prorationDateTime = null;
      state.gettingProrationCost = true;
      state.getProrationCostError = false;
      state.getProrationCostErrorMessage = "";
    },
    getProrationCostFailed(state, action: PayloadAction<string>) {
      state.prorationCost = null;
      state.prorationToken = null;
      state.prorationPlanToId = "";
      state.gettingProrationCost = false;
      state.getProrationCostError = true;
      state.getProrationCostErrorMessage = action.payload;
    },
    getProrationCostSuccess(
      state,
      action: PayloadAction<{ cost: number; prorationDateTime: number; prorationToken: string }>
    ) {
      state.prorationCost = action.payload.cost;
      state.prorationDateTime = action.payload.prorationDateTime;
      state.prorationToken = action.payload.prorationToken;
      state.gettingProrationCost = false;
      state.getProrationCostError = false;
      state.getProrationCostErrorMessage = "";
    },
    submittingUpgradeSubscription(state) {
      state.submittingUpgradeSubscription = true;
      state.upgradeSubscriptionModalFailed = false;
      state.upgradeSubscriptionModalFailedMessage = "";
    },
    submitUpgradeSubscriptionFailed(state, action: PayloadAction<string>) {
      state.submittingUpgradeSubscription = false;
      state.upgradeSubscriptionModalFailed = true;
      state.upgradeSubscriptionModalFailedMessage = action.payload;
    },
    submitUpgradeSubscriptionSuccess(state) {
      state.submittingUpgradeSubscription = false;
      state.upgradeSubscriptionModalFailed = false;
      state.upgradeSubscriptionModalFailedMessage = "";
      state.prorationCost = null;
      state.prorationPlanToId = "";
      state.prorationDateTime = null;
    },
    gettingCurrentPencilerSubscription(state) {
      state.currentPencilerSubscription = {};
      state.gettingCurrentPencilerSubscription = true;
      state.getCurrentPencilerSubscriptionSuccess = false;
      state.getCurrentPencilerSubscriptionError = false;
      state.getCurrentPencilerSubscriptionErrorMessage = "";
    },
    getCurrentPencilerSubscriptionSuccess(state, action: PayloadAction<PencilerSubscription>) {
      state.currentPencilerSubscription = action.payload;
      state.gettingCurrentPencilerSubscription = false;
      state.getCurrentPencilerSubscriptionSuccess = true;
      state.getCurrentPencilerSubscriptionError = false;
      state.getCurrentPencilerSubscriptionErrorMessage = "";
    },
    getCurrentPencilerSubscriptionFailed(state, action: PayloadAction<string>) {
      state.currentPencilerSubscription = {};
      state.gettingCurrentPencilerSubscription = false;
      state.getCurrentPencilerSubscriptionSuccess = false;
      state.getCurrentPencilerSubscriptionError = true;
      state.getCurrentPencilerSubscriptionErrorMessage = action.payload;
    }
  }
});

export const {
  gettingSubscriptionOptions,
  getSubscriptionOptionsSuccess,
  getSubscriptionOptionsFailed,
  setAddSubscriptionModalStatus,
  addSubscriptionCheckoutFailed,
  setUpgradeSubscriptionModalStatus,
  upgradeSubscriptionCheckoutFailed,
  gettingProrationCost,
  getProrationCostFailed,
  submittingUpgradeSubscription,
  submitUpgradeSubscriptionFailed,
  submitUpgradeSubscriptionSuccess,
  gettingCurrentPencilerSubscription,
  getCurrentPencilerSubscriptionSuccess,
  getCurrentPencilerSubscriptionFailed
} = subscriptionsSlice.actions;

export const getProrationCostSuccess = (
  cost: number,
  prorationDateTime: number,
  prorationToken: string
) => (dispatch: AppDispatch) =>
  dispatch(
    subscriptionsSlice.actions.getProrationCostSuccess({ cost, prorationDateTime, prorationToken })
  );

export default subscriptionsSlice.reducer;
